import { ChangeEvent, FC, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { CurrencySelect } from "../../components/common";
import { ArchwayDetails } from "../../components/forms/invoice/ArchwayDetails";
import { NewWalletModal } from "../../components/modals";
import { WalletDropdown } from "../../components/wallet";
import {
  DEFAULT_CRYPTO_CURRENCY,
  DEFAULT_FIAT_CURRENCY,
} from "../../constants";
import DashboardLayout from "../../layouts/Dashboard.layout";
import { selectOnramps } from "../../store/reducers/onramps";
import { selectCurrentUser } from "../../store/reducers/user";
import { selectWallets } from "../../store/reducers/wallets";
import {
  useGetOnrampsQuery,
  useUpdateOnrampMutation,
} from "../../store/services/onramps";
import { useGetWalletsQuery } from "../../store/services/wallets";
import { Currency, CurrencyType } from "../../types/InvoiceTypes";
import { Wallet } from "../../types/WalletTypes";
import { formatAmount } from "../../utils/numbers/money";
import { toastSuccess } from "../../utils/toasts";

export const BuyPage: FC = () => {
  const [selectedFiatCurrency, setSelectedFiatCurrency] = useState<Currency>(
    DEFAULT_FIAT_CURRENCY
  );

  const { allowed_incoming_currencies } = useSelector(selectCurrentUser);
  const [selectedWallet, setSelectedWallet] = useState<Wallet>();
  useGetOnrampsQuery();
  const onramps = useSelector(selectOnramps);
  useGetWalletsQuery();
  const walletList = useSelector(selectWallets);
  const cryptoWallets = walletList?.filter((wallet) => !!wallet.address);
  const [updateOnramp] = useUpdateOnrampMutation();
  const [showWalletForm, setShowWalletForm] = useState(false);

  useEffect(() => {
    if (onramps) {
      const foundWallet = cryptoWallets?.find(
        ({ id }) => id === onramps.wallet_id
      );

      if (foundWallet) {
        setSelectedWallet(foundWallet);
      }
    }
  }, [onramps, cryptoWallets]);

  const selectedPaymentDetails = onramps?.bank_details?.find(
    (detail) => detail.currency === selectedFiatCurrency
  );

  const onSelectWallet = (wallet: Wallet) => {
    updateOnramp({
      wallet_id: wallet.id!,
      outgoing_currency: DEFAULT_CRYPTO_CURRENCY,
    });
  };

  const CurrencySelector = (
    <CurrencySelect
      name="incoming_currency"
      allowedCurrencies={allowed_incoming_currencies}
      value={selectedFiatCurrency}
      currencyType={CurrencyType.FIAT}
      onChange={(e: ChangeEvent<HTMLInputElement>) => {
        setSelectedFiatCurrency(e.target.value as Currency);
      }}
    />
  );

  const paymentDetailsMessage = useMemo(() => {
    const sepaText =
      selectedFiatCurrency === Currency.EUR ? (
        <>
          {" "}
          through <span className="font-extrabold">SEPA/SEPA-Instant only</span>
        </>
      ) : undefined;

    return (
      <>
        Every time you send{" "}
        <span className="font-extrabold uppercase">{selectedFiatCurrency}</span>
        {sepaText} to these bank details, we payout USDT to your wallet.{" "}
        <span className="font-extrabold">IMPORTANT</span>: Payment must come
        from your own bank account and include the{" "}
        <span className="font-extrabold">REF # / MEMO / Comment</span>.
      </>
    );
  }, [selectedFiatCurrency]);

  const monthlyLimit = parseFloat(onramps?.eur_monthly_limit || "0");
  const monthTotal = parseFloat(onramps?.month_eur_total || "0");
  const monthlyLeft = monthlyLimit - monthTotal;

  return (
    <DashboardLayout title="Buy stablecoins" action={CurrencySelector}>
      <div className="mb-4 md:mb-16 bg-white px-4 md:px-8 pt-4 md:pt-8 pb-8 md:pb-16 rounded-2xl border border-gray-100">
        <h3 className="text-gray-500 font-source-serif text-5xl pb-16">
          Stablecoins destination wallet
        </h3>

        <WalletDropdown
          values={cryptoWallets}
          value={selectedWallet}
          onSelect={onSelectWallet}
          readonly={!!selectedWallet}
          onCreate={() => setShowWalletForm(true)}
        />

        <NewWalletModal
          isOpen={showWalletForm}
          close={() => setShowWalletForm(false)}
          callback={() => {
            toastSuccess("Wallet created successfully");
            setShowWalletForm(false);
          }}
        />
      </div>

      <div className="mb-16 bg-white px-4 md:px-8 pt-4 md:pt-8 pb-4 md:pb-16 rounded-2xl border border-gray-100">
        <ArchwayDetails
          show={!!selectedPaymentDetails}
          currency={selectedFiatCurrency}
          message={paymentDetailsMessage}
          data={{
            ...selectedPaymentDetails,
          }}
        />

        <div className="flex flex-row justify-between items-center pt-8 border-gray-50 border-b pb-4 mb-4">
          <div className="theme-caption uppercase basis-2/3">
            Instruction for onramp
          </div>

          <div className="theme-caption uppercase basis-1/3">
            Monthly limit left
          </div>
        </div>

        <div className="flex flex-row justify-between items-center gap-4">
          <div className="basis-2/3">
            You can exchange up to &nbsp;
            {formatAmount(monthlyLimit)}
            &nbsp; EUR or equivalent in other currencies within this calendar
            month. Get in touch with &nbsp;
            <a
              href="mailto:payments@archway.finance"
              className="text-skyblue-700"
            >
              payments@archway.finance
            </a>
            &nbsp; to increase your limits.
          </div>

          <div className="basis-1/3">{formatAmount(monthlyLeft)}</div>
        </div>
      </div>
    </DashboardLayout>
  );
};
